/**
 * The words the plan screen uses, kept out of it.
 *
 * The same arrangement `copy/generation.ts` has, and the same reason: a screen
 * that renders an enum value renders the server's word for something, and the
 * server's word is chosen for a schema rather than for a person. `ai_call` is
 * accurate and tells a studio nothing about what they are being counted for.
 */

import type { AllowanceState, Overage, Plan, PlanProjection } from "@magicweave/api";

/** What each meter is, in the words a studio would use for it. */
export const METER_WORDS: Record<string, string> = {
  ai_call: "Asking a model something",
  simulation: "Simulating an economy",
  redemption: "Redeeming a voucher",
  tool_call: "Tool calls",
  // Retired — rows written before the asset generation feature (and then the
  // meters that replaced it) were removed still carry these, and they are
  // still part of the total. See `UsageLine.meter`.
  asset_generation: "Generating an asset",
  asset_sound: "Generating a sound",
  asset_voice: "Voice lines",
  asset_byo: "Generating with your own model",
};

/**
 * An amount in minor units, as money.
 *
 * Minor units all the way from the database, so the division belongs here and
 * nowhere else. `Intl` does the currency's own conventions, including how many
 * decimal places it actually has — which is not always two, and is the bug a
 * hard-coded `/100` ships with.
 */
export function money(minorUnits: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(
      minorUnits / 100,
    );
  } catch {
    // An unrecognised currency code is a server newer than this build, and a
    // page that throws over one is worse than a page showing the code itself.
    return `${(minorUnits / 100).toFixed(2)} ${currency}`;
  }
}

/** A count in Indian digit grouping, "2,00,000": the one way a figure reads on every billing screen. */
export function grouped(n: number): string {
  return n.toLocaleString("en-IN");
}

/**
 * Whether a move to `plan` is an upgrade, the way billing decides it: anything
 * not cheaper than what is in force starts today and is charged now. Only a
 * cheaper plan is a downgrade, scheduled for the end of the month.
 */
export function isUpgrade(
  plan: Pick<Plan, "price">,
  current: Pick<Plan, "price"> | null | undefined,
) {
  return plan.price >= (current?.price ?? 0);
}

/**
 * Whether a plan's button and the checkout's title say "Upgrade": billing
 * calls it an upgrade and it is a different plan. A newer version of the plan
 * already in force is the same tier, so it reads as a move even though it is
 * charged the way an upgrade is.
 */
export function readsAsUpgrade(
  plan: Pick<Plan, "price" | "code">,
  current: Pick<Plan, "price" | "code"> | null | undefined,
) {
  return isUpgrade(plan, current) && current?.code !== plan.code;
}

/** "Upgrade to Pro", "Move to Indie": the one verb on a plan's button and the checkout title. */
export function moveVerb(
  plan: Pick<Plan, "price" | "code" | "name">,
  current: Pick<Plan, "price" | "code"> | null | undefined,
): string {
  return `${readsAsUpgrade(plan, current) ? "Upgrade" : "Move"} to ${plan.name}`;
}

/**
 * "10×", for how many times more a plan holds than the one in force: rounded,
 * and only when it is at least twice as much. A smaller gain is not worth a badge.
 */
export function timesMore(next: number | null | undefined, now: number | null | undefined) {
  if (!next || !now) return undefined;
  const ratio = next / now;
  return ratio >= 2 ? `${String(Math.round(ratio))}×` : undefined;
}

/** What one coin minted costs, in minor units of the billing currency; nought when the plan mints none. */
export function coinMinor(
  caps: Pick<Plan["capabilities"], "coin_price" | "coin_price_usd">,
  currency: string,
): number {
  return (currency === "USD" ? caps.coin_price_usd : caps.coin_price) ?? 0;
}

/** What a plan's coin costs a studio in its currency, "₹1"; undefined when it mints none. */
export function coinPrice(
  caps: Pick<Plan["capabilities"], "coin_price" | "coin_price_usd">,
  currency: string,
): string | undefined {
  const minor = coinMinor(caps, currency);
  return minor > 0 ? coinPriceWords(minor, currency) : undefined;
}

// Said the same on Plan, Usage and Checkout, so each is written once.
/** The players line: "of 10,000 · ever". */
export function playersOfWords(limit: number): string {
  return `of ${grouped(limit)} · ever`;
}
/** The development players line: "of 200 · last 30 days". */
export const DEV_WINDOW = "last 30 days";
export function devOfWords(limit: number): string {
  return `of ${grouped(limit)} · ${DEV_WINDOW}`;
}
/** "left of 10,000 · renews 1 Nov", or "one-time" for credits given once. */
export function creditsOfWords(total: number, renews: string | undefined, lifetime: boolean) {
  if (lifetime) return `left of ${grouped(total)} one-time`;
  return `left of ${grouped(total)}${renews ? ` · renews ${renews}` : ""}`;
}
export const SEATS_OVER = "More people than seats";
export const LIMITS_LIVE = "Your new limits are live.";
/** Where a person who is stuck goes. No button until a support page is agreed. */
export const GET_IN_TOUCH = "Get in touch and mention your studio.";

/**
 * How a plan's member limit reads.
 *
 * The same absent-means-unlimited rule `capWords` states for testing
 * environments, and its own function rather than a shared one: `capWords`
 * says "as many as you like" of a control a studio presses; a seat is a
 * person, and "Unlimited" is the word this screen's other seat sentences
 * already use.
 */
export function seatCapWords(max: number | null | undefined): string {
  return max === null || max === undefined ? "Unlimited" : grouped(max);
}

/**
 * A coin's price in minor units, the way a price is said: "₹1" for a whole
 * unit and "$0.01" for anything that is not, rather than ".00" on every figure.
 */
export function coinPriceWords(minorUnits: number, currency: string): string {
  const whole = minorUnits % 100 === 0;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(minorUnits / 100);
  } catch {
    return `${(minorUnits / 100).toFixed(2)} ${currency}`;
  }
}

export { countedPlayers, playerStanding } from "./player-standing";

/**
 * Development players against the plan's rolling 30-day cap, or null when the
 * plan has none — a meter against nothing is not drawn. `full` is the gate's
 * own comparison: the server refuses at `nonprod_last_30d >= nonprod_limit`.
 */
export function devPlayerStanding(usage: {
  nonprod_last_30d: number;
  nonprod_limit: number | null;
}): { share: number; full: boolean } | null {
  const limit = usage.nonprod_limit;
  if (limit === null || limit <= 0) return null;
  return {
    share: Math.min(usage.nonprod_last_30d / limit, 1),
    full: usage.nonprod_last_30d >= limit,
  };
}

/** A plan card's line for `free_viewers`. */
export const VIEWERS_FREE_LINE = "View-only members free";

/**
 * The multiple a plan prices overage at, as a person reads it.
 *
 * Basis points on the wire so that a multiple is exact rather than a float
 * somebody rounded, and 10,000 of them is one whole multiple — so 25,000 is
 * 2.5×, which is the figure `decisions/0025` decision 6 prices overage at. A
 * unit conversion and not a derivation: the multiple itself is the plan's, and
 * nothing here works out what a credit costs from it.
 */
export function multipleWords(basisPoints: number): string {
  return `${basisPoints / 10_000}×`;
}

/**
 * What a credit past the allowance costs, in the unit this screen quotes.
 *
 * A thousand credits, because quoting per credit rounds a real price to nothing
 * — 2.5× the largest plan's bundle is four paise. **The figure arrives derived**
 * from the plan's own bundled rate and its multiple; this puts a unit on it and
 * does no arithmetic of its own. A console multiplying a price by a multiple
 * would be a second spelling of the number the invoice will carry, which is the
 * disagreement decision 6 put the derived rate on the wire to end.
 */
export function rateWords(ratePerThousandCredits: number, currency: string): string {
  return `${money(ratePerThousandCredits, currency)} per 1,000 AI credits`;
}

/**
 * Whether a ceiling on this plan would buy any credits at all.
 *
 * **No plan is named here and none is tested for.** `decisions/0025` decision 6
 * keeps a free tier out of overage "structurally rather than by a rule: its
 * multiple is zero, and a multiple of zero *is* the cap". So this reads the
 * price the server sent: a plan pricing nothing past its allowance reads as
 * unavailable whatever it is called, and a plan that starts pricing it becomes
 * available with nothing here changing.
 *
 * The rate is read as well as the multiple because they are one wall arriving
 * twice — a plan bundling no credits to price against has a rate of nought
 * however large its multiple — and the server answers `raise_plan` to both.
 */
export function overagePriced(overage: Overage): boolean {
  return overage.multiple_basis_points > 0 && overage.rate_per_thousand_credits > 0;
}

/**
 * Why a ceiling would buy nothing, said without naming a plan.
 *
 * It describes what this plan prices, which is a fact about the plan in force
 * and true of any plan that prices nothing. A sentence naming the free tier
 * would be the rule the decision declines to write, spelled in prose instead of
 * in code.
 */
export const OVERAGE_UNPRICED =
  "This plan prices no AI credits past its allowance, so a ceiling of any size would buy none of them. A larger plan is the only way to carry on once its AI credits are gone.";

/**
 * A ceiling as somebody typed it, in minor units, or null for nothing usable.
 *
 * Whole units of the currency in, minor units out, which is the same hundredth
 * `money` divides by on the way back. Null covers an empty field and anything
 * that is not a non-negative number, and the caller decides what that means:
 * empty with the switch off is a studio clearing the figure, and empty with the
 * switch on is the blank cheque decision 6 refuses.
 */
export function parseCeiling(typed: string): number | null {
  const trimmed = typed.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100);
}

/**
 * What each kind of credit is, in the words a studio would use for it.
 *
 * Two kinds and they are not the same credit: one bought something we delivered
 * as it was recorded, and one turned into a coin a player holds and a studio
 * will honour later. Both come off the same allowance, which is why they are
 * drawn as two series over one month rather than added into one line — a chart
 * with a single figure per day would say they were one thing.
 */
export const SPEND_WORDS: Record<string, string> = {
  consumed: "AI credits spent on AI work",
  // Retired: coins are billed per coin minted now, not bought with credits.
  // Rows from before still carry it.
  converted: "Credits once turned into coins",
};

/**
 * A forecast, said in one line.
 *
 * **This is the half of the packet that is prose rather than data.** The server
 * sends a figure, what it was measured over, and where that total would leave
 * the account; a number rendered alone is a number a studio will take for a
 * reading of their month. `headline` is the figure and the day it is for,
 * `consequence` is the only reason anybody wanted it, and `basis` is the hedge:
 * what it was worked out from and that it is not a measurement. The hedge rides
 * on the "Projected" badge as its tooltip so the line stays one line.
 *
 * **`consequence` derives nothing.** It is a phrase per `allowance.state`, and
 * the state was derived on the server from the projected total by the same
 * function that derives the live one. A comparison here between `credits` and
 * the plan's allowance would be a second definition of when a month runs out,
 * on the screen that is supposed to agree with the chip and the email.
 */
export interface ProjectionWords {
  /** "About 4,805 by 31 Aug". Never a bare number. */
  headline: string;
  /** What it was worked out from, and that it is not a measurement. */
  basis: string;
  /** What that pace would mean, from the state the server derived for it. */
  consequence: string;
}

export function projectionWords(projection: PlanProjection, endsOn: string): ProjectionWords {
  return {
    headline: `About ${grouped(projection.credits)} by ${endsOn}`,
    basis:
      `An estimate from the first ${projection.observed_days} ` +
      `${projection.observed_days === 1 ? "day" : "days"} of a ${projection.period_days}-day month, ` +
      `at the pace they were spent at. It is not a measurement, and a month rarely runs at one pace.`,
    consequence: consequenceWords(projection.allowance.state),
  };
}

/**
 * What this pace would mean, a few words per rung.
 *
 * A switch with no default, so a sixth state is a type error here rather than a
 * forecast that renders as nothing on the screen where somebody is deciding
 * whether to buy a bigger plan.
 */
function consequenceWords(state: AllowanceState): string {
  switch (state) {
    case "comfortable":
      return "inside your allowance";
    case "low":
      return "leaves very little";
    case "spent":
      return "runs out early";
    case "over":
      return "past your allowance";
    case "capped":
      return "reaches your ceiling";
  }
}
